import { ReactElement, ReactNode } from 'react'
import { Box, Tooltip, useTheme } from '@mui/material'
import {
  OVERUTILIZED_CHIP_BACKGROUND,
  isUtilizationError,
  isUtilizationWarning,
} from '../../../utils'

const numberFormatter = new Intl.NumberFormat('de-DE', {
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
})

const fmt = (value: number) => numberFormatter.format(value)

type EtaValueProps = {
  value?: number
  allowedOverutilizationPercent?: number
  muted?: boolean
  tooltip: ReactNode
  hideUnavailable?: boolean
  /** Render "ERR" and use the over-utilized colour (for failed binary checks like Schwingung). */
  isError?: boolean
}

const EtaValue = ({
  value,
  allowedOverutilizationPercent = 0,
  muted,
  tooltip,
  hideUnavailable,
  isError,
}: EtaValueProps): ReactElement => {
  const theme = useTheme()
  const available = value !== undefined && Number.isFinite(value)
  const over = isError || (available && isUtilizationError(value, allowedOverutilizationPercent))
  const near =
    !isError && available && !over && isUtilizationWarning(value, allowedOverutilizationPercent)

  const background = over ? OVERUTILIZED_CHIP_BACKGROUND : near ? '#FFF1C9' : 'transparent'
  const color = over
    ? theme.palette.warning.dark
    : near
    ? '#7A5A00'
    : muted
    ? theme.palette.text.secondary
    : theme.palette.text.primary

  return (
    <Tooltip title={tooltip} placement="right" arrow disableInteractive>
      <Box
        component="span"
        sx={{
          fontSize: muted ? 12 : 13,
          fontWeight: 500,
          px: 0.5,
          borderRadius: 0.25,
          background,
          color,
        }}
      >
        {isError ? 'ERR' : available ? fmt(value) : hideUnavailable ? '' : 'n/a'}
      </Box>
    </Tooltip>
  )
}

const EtaShortLabel = ({ short, tooltip }: { short: string; tooltip?: string }): ReactElement => {
  const theme = useTheme()
  return (
    <Tooltip title={tooltip ?? ''} placement="left" arrow disableInteractive>
      <Box
        component="span"
        sx={{
          fontSize: 11,
          fontWeight: 500,
          color: theme.palette.text.secondary,
          lineHeight: 1.2,
        }}
      >
        {short}
      </Box>
    </Tooltip>
  )
}

export const EtaStack = ({
  eta,
  etaC,
  etaShort,
  etaShortTooltip,
  etaAllowedOverutilizationPercent,
  etaCAllowedOverutilizationPercent,
  hideUnavailable,
  etaIsError,
}: {
  eta?: number
  etaC?: number
  /** Short label of the check governing η (M, N, Q, …), shown before the η value. */
  etaShort?: string
  /** Full readable category of the η-governing check — short label's tooltip. */
  etaShortTooltip?: string
  etaAllowedOverutilizationPercent?: number
  etaCAllowedOverutilizationPercent?: number
  hideUnavailable?: boolean
  etaIsError?: boolean
}): ReactElement => (
  <Box
    sx={{
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'flex-end',
      gap: 0.25,
      flexShrink: 0,
      lineHeight: 1.2,
      minWidth: 40,
    }}
  >
    <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 0.5 }}>
      {etaShort ? <EtaShortLabel short={etaShort} tooltip={etaShortTooltip} /> : null}
      <EtaValue
        value={eta}
        allowedOverutilizationPercent={etaAllowedOverutilizationPercent}
        hideUnavailable={hideUnavailable}
        isError={etaIsError}
        tooltip={<Box sx={{ fontFamily: 'Roboto', fontSize: 12 }}>η</Box>}
      />
    </Box>
    {etaC !== undefined ? (
      <EtaValue
        value={etaC}
        allowedOverutilizationPercent={etaCAllowedOverutilizationPercent}
        muted
        hideUnavailable={hideUnavailable}
        tooltip={
          <Box sx={{ fontFamily: 'Roboto', fontSize: 12 }}>
            η
            <Box component="sub" sx={{ fontSize: 9, ml: 0.25 }}>
              c,90
            </Box>
          </Box>
        }
      />
    ) : null}
  </Box>
)
